import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import {
  Alert,
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import { useSetAtom } from 'jotai';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { chatbotPrimeTexts } from 'src/components/chatbot/atoms';
import { useCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import DashboardCommentsProvider from 'src/components/dashboard/DashboardContext/CommentsContext';
import {
  hideWidget,
  showWidget,
} from 'src/components/dashboard/DashboardPage/components/WidgetWrapper/helper';
import OpenCommentsFilter from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/Filter';
import {
  applySorting,
  filterSentimental,
  filterTopic,
  generateChatBotTexts,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/helper';
import renderTree from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/renderTree';

import { styles } from './index.styles';
import { styles as treeItemStyles } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/TreeItem/index.styles';

export const OPEN_COMMENTS_ROOT_TEST_ID = 'open-comments-root-test-id';

const TOPICS_CUTOFF = 30;

export const legendLabels = [
  'negativeTopics',
  'neutralTopics',
  'positiveTopics',
];

const OpenCommentsWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const setChatbotPrimeTexts = useSetAtom(chatbotPrimeTexts('fakeChat'));
  const { groupedCodingResults, noData, errorMsg, filter, setFilter, loading } =
    useCodingResults();
  const [filteredCodingResults, highestCount, totalAmount] = useMemo(() => {
    const { topicFilter, sentimentFilter, sorting } = filter;

    let result = [...groupedCodingResults];
    result = filterTopic(result, topicFilter);
    result = filterSentimental(result, sentimentFilter);

    const totalAmount = result.length;
    result = applySorting(result, sorting, sentimentFilter, TOPICS_CUTOFF);

    const highestCount = result.reduce(
      (acc, gcr) => (gcr[sentimentFilter] > acc ? gcr[sentimentFilter] : acc),
      0,
    );

    return [result, highestCount, totalAmount];
  }, [filter, groupedCodingResults]);

  const previousResultAmountRef = useRef(10);
  useEffect(() => {
    previousResultAmountRef.current = groupedCodingResults.length || 10;
  }, [groupedCodingResults]);

  const { createStep, removeStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => !noData.initial && createStep(config.id, config.guided_tour_step),
    [noData, config.guided_tour_step],
  );

  const widgetRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (noData.initial && !loading) {
      removeStep(config.id);
      widgetRef.current = hideWidget(config.id, widgetRef.current);
    } else {
      showWidget(config.id, widgetRef.current);
    }
  }, [noData, loading]);

  useEffect(() => {
    const primeText = generateChatBotTexts([...groupedCodingResults]);
    if (primeText) {
      setChatbotPrimeTexts({
        openCommentsWidget: {
          primeQuestion: 'What else is important?',
          primeText:
            `From the employees' point of view, there is also ` +
            `potential for improvement in the areas of ${primeText}.`,
        },
      });
    }
  }, [groupedCodingResults]);

  // Do not render this widget if the reporting engine did not return any data
  if (noData.initial) {
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1, maxWidth: '100%' }}>
      <Grid
        container
        data-test-id={OPEN_COMMENTS_ROOT_TEST_ID}
        spacing={2}
        sx={{ p: 2 }}
      >
        <Grid item xs={12}>
          <Stack direction="row" spacing={1.25}>
            {guidedTour}
            <Typography variant="h3">
              {config.name ? config.name : t('OpenComments.openComments')}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <OpenCommentsFilter
            setOpenCommentFilter={setFilter}
            openCommentFilter={filter}
            withoutRelevance={config.hide_sort_by_relevance}
          />
        </Grid>

        {upMd && (
          <>
            <Grid item xs={3} />
            <Grid item xs={9}>
              <Box sx={{ marginBottom: 2, ...styles.legend }}>
                {legendLabels.map((legendLabel, index) => (
                  <Typography
                    key={legendLabel}
                    sx={styles.legendLabel(index)}
                    variant="body2"
                  >
                    {t(`OpenComments.${legendLabel}`)}
                  </Typography>
                ))}
              </Box>
            </Grid>
          </>
        )}
        {filter.sorting === 'relevance' && (
          <Stack spacing={3} direction="row" sx={styles.relevanceLegend}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Box sx={treeItemStyles.medalRelevanceIcon} />
              <Typography variant="caption2">
                {t('OpenComments.legendText')}
              </Typography>
            </Stack>
            {totalAmount > TOPICS_CUTOFF && (
              <Box sx={styles.hint}>
                <Typography variant="caption2">
                  {t('OpenComments.cutoffTopics', {
                    amount: TOPICS_CUTOFF,
                  })}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        <Grid item xs={12}>
          {loading ? (
            <Stack direction="column" spacing={1}>
              {Array(previousResultAmountRef.current)
                .fill(null)
                .map((_, i) => (
                  <Stack direction="row" spacing={3} key={i}>
                    <Skeleton variant="rectangular" height={28} width="25%" />
                    <Skeleton variant="rectangular" height={28} width="75%" />
                  </Stack>
                ))}
            </Stack>
          ) : !errorMsg ? (
            !noData.filtered ? (
              <DashboardCommentsProvider source="topics">
                <SimpleTreeView
                  aria-label="icon expansion"
                  slots={{
                    expandIcon: () => (
                      <FaPlusCircle style={{ marginRight: '0.5rem' }} />
                    ),
                    collapseIcon: () => (
                      <FaMinusCircle style={{ marginRight: '0.5rem' }} />
                    ),
                  }}
                >
                  {filteredCodingResults.map((category) =>
                    renderTree(
                      category,
                      0,
                      highestCount,
                      config.questions?.[0],
                      filter,
                      'default',
                      config.show_translation_toggle ?? true,
                    ),
                  )}
                </SimpleTreeView>
              </DashboardCommentsProvider>
            ) : (
              <Stack direction="row" sx={{ justifyContent: 'center', py: 10 }}>
                <Alert severity="error" icon={false} sx={styles.alert}>
                  {t('common:noResults')}
                </Alert>
              </Stack>
            )
          ) : (
            <Box sx={styles.errorMsg}>{errorMsg}</Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default OpenCommentsWidget;
